<!--
  Модалка настройки таблицы: сохраненные конфигурации, состав колонок
  и порядок отображения полей
-->
<template>
  <BaseModal
    :visible="true"
    title="Настройки таблицы"
    size="wide"
    body-padding="none"
    @close="$emit('close')"
  >
    <base-loader v-if="isLoading" />

    <div class="settings-body" v-else>
      <div
        class="sidebar"
        :class="{ 'mobile-hidden': isMobileEditing }"
      >
        <div class="sidebar-header">
          <h4>Сохраненные конфигурации</h4>
          <base-primary-button
            class="add-btn"
            @click="createNewConfig"
            :disabled="isCreatingConfig"
          >
            <span v-if="isCreatingConfig">...</span>
            <span v-else>+</span>
          </base-primary-button>
        </div>

        <ul class="config-list custom-scroll">
          <li
            v-for="config in configs"
            :key="config.id"
            class="config-item"
            :class="{
              active: selectedConfigId === config.id,
              'current-config': config.current,
            }"
            @click="selectConfig(config)"
          >
            <div class="config-info">
              <span class="config-name">{{ config.name }}</span>
              <div class="badges">
                <span
                  v-if="config.default"
                  class="badge badge-default"
                >
                  Стандартный
                </span>
                <span
                  v-if="config.current"
                  class="badge badge-current"
                >
                  Активен
                </span>
              </div>
            </div>
            <div class="config-actions">
              <button
                v-if="!config.current"
                class="icon-btn apply-btn"
                @click.stop="applyConfig(config)"
                title="Применить конфигурацию"
              >
                <AppIcon name="check" :size="16" />
              </button>
              <button
                v-if="!config.default"
                class="icon-btn delete"
                @click.stop="deleteConfig(config)"
                :title="getDeleteTooltip(config)"
                :disabled="!canDeleteConfig(config)"
              >
                <AppIcon name="trash" :size="16" />
              </button>
            </div>
          </li>
          <li v-if="!configs.length" class="config-item empty">
            Нет сохраненных конфигураций
          </li>
        </ul>
      </div>

      <div
        class="editor-panel"
        :class="{ 'mobile-visible': isMobileEditing }"
      >
        <div class="mobile-nav" v-if="isMobileEditing">
          <button @click="isMobileEditing = false">
            ← Назад к списку
          </button>
        </div>

        <form @submit.prevent="saveConfig" class="editor-form">
          <div class="editor-header-inputs">
            <div class="form-group name-input">
              <base-label>Название вида</base-label>
              <base-input
                v-model="editingConfig.name"
                placeholder="Например: Мой список"
                required
              />
            </div>
          </div>

          <div class="editor-workspace">
            <div class="workspace-col active-fields-col">
              <div class="col-header">
                <h5>Отображаемые колонки</h5>
                <span class="count">
                  {{ enabledFieldsCount }}
                </span>
              </div>

              <div class="fields-list-labels">
                <span class="label-col">Название</span>
                <div class="label-props">
                  <span>Px</span>
                  <span>Вкл</span>
                </div>
              </div>

              <div class="fields-container custom-scroll">
                <div
                  v-for="(field, index) in editingFields"
                  :key="field.key + '_' + index"
                  class="field-item"
                  draggable="true"
                  @dragstart="onDragStart($event, index)"
                  @dragover.prevent
                  @drop="onDrop($event, index)"
                  @dragenter.prevent
                >
                  <div class="drag-handle" title="Перетащить">
                    <AppIcon name="grip" :size="12" />
                  </div>

                  <div class="field-content">
                    <div class="field-main">
                      <input
                        type="text"
                        v-model="field.label"
                        class="field-name-input"
                        :placeholder="field.key"
                        @blur="updateFieldLabel(field)"
                      />
                    </div>

                    <div class="field-props">
                      <input
                        type="number"
                        v-model="field.width"
                        class="width-input"
                        min="30"
                        @blur="updateFieldWidth(field)"
                      />
                      <label
                        class="toggle-switch"
                        v-if="!field.isNewCustom && field.id"
                      >
                        <input
                          type="checkbox"
                          v-model="field.enabled"
                          @change="toggleFieldEnabled(field)"
                          :disabled="field.updating"
                        />
                        <span class="slider"></span>
                        <span
                          v-if="field.updating"
                          class="switch-loader"
                        ></span>
                      </label>
                      <div
                        v-else
                        class="always-enabled-indicator"
                        title="Кастомное поле включено"
                      >
                        <AppIcon name="check" :size="12" />
                      </div>
                    </div>

                    <button
                      class="field-delete-btn"
                      @click="deleteField(field, index)"
                      title="Удалить поле из конфигурации"
                    >
                      <AppIcon name="close" :size="14" />
                    </button>
                  </div>
                </div>

                <div
                  v-if="editingFields.length === 0"
                  class="empty-placeholder"
                >
                  Нет активных колонок. Выберите из списка справа.
                </div>
              </div>
            </div>

            <div class="workspace-col available-fields-col">
              <div class="col-header">
                <h5>Добавить поля</h5>
                <div class="col-header-right">
                  <base-loader
                    v-if="loadingAttributes"
                    :small="true"
                  />
                  <span v-else class="count">
                    {{ filteredAttributes.length }}
                  </span>
                </div>
              </div>

              <div class="search-box">
                <AppIcon
                  name="search"
                  class="search-icon"
                  :size="14"
                />
                <input
                  type="text"
                  v-model="attributeSearch"
                  placeholder="Поиск по полям..."
                />
              </div>

              <div class="attributes-list custom-scroll">
                <div
                  v-for="attr in filteredAttributes"
                  :key="attr.key"
                  class="attribute-item"
                  @click="addAttribute(attr)"
                  :title="attr.key"
                >
                  <div class="attr-icon">+</div>
                  <div class="attr-info">
                    <span class="attr-name">
                      {{ attr.label || attr.name }}
                    </span>
                    <span class="attr-key">
                      {{ attr.key }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="
                    !loadingAttributes &&
                    filteredAttributes.length === 0
                  "
                  class="empty-search"
                >
                  <span v-if="attributeSearch">
                    Ничего не найдено
                  </span>
                  <span v-else>Все доступные поля уже добавлены</span>
                </div>
              </div>
            </div>
          </div>

          <div class="form-actions sticky-footer">
            <base-button
              type="button"
              @click="resetToDefaults"
              class="reset-btn"
            >
              Сбросить
            </base-button>
            <base-primary-button type="submit" :disabled="isSaving">
              <span v-if="isSaving">Сохранение...</span>
              <span v-else>Сохранить изменения</span>
            </base-primary-button>
          </div>
        </form>
      </div>
    </div>
  </BaseModal>

  <BaseDialog
    :visible="Boolean(configPendingDelete)"
    title="Удалить конфигурацию?"
    :message="deleteConfigDialogMessage"
    :actions="[
      { key: 'confirm', label: 'Удалить', type: 'danger' },
      { key: 'close', label: 'Отмена', type: 'secondary' },
    ]"
    @action="handleDeleteConfigDialogAction"
  />

  <BaseDialog
    :visible="showResetDialog"
    title="Сбросить конфигурацию?"
    message="Все пользовательские настройки колонок будут удалены"
    :actions="[
      { key: 'confirm', label: 'Сбросить', type: 'danger' },
      { key: 'close', label: 'Отмена', type: 'secondary' },
    ]"
    @action="handleResetDialogAction"
  />
</template>

<script>
import { useTableConfigStore } from "@/stores/tableConfigStore";
import { useNotificationStore } from "@/stores/notificationStore";

import tableConfigsApi from "@/services/api/tableConfigsApi";

import { getCompanyId } from "@/utils/userUtils";
import { loadTableColumns } from "@/utils/tableFormatter";

import AppIcon from "@/components/Icon/AppIcon.vue";

export default {
  name: "TableSettingsModal",
  components: {
    AppIcon,
  },
  props: {
    resourceModel: { type: String, required: true },
    recordType: { type: String, required: false },
    tableName: { type: String, required: true },
  },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      isCreatingConfig: false,
      isDeletingField: false,
      loadingAttributes: false,
      companyId: getCompanyId(),
      configs: [],
      selectedConfigId: null,
      editingConfig: {
        id: null,
        name: "",
        default: false,
        resource_model: "",
      },
      editingFields: [],
      availableAttributes: [],
      attributeSearch: "",
      schemaColumns: [],
      draggedIndex: null,
      isMobileEditing: false,
      configPendingDelete: null,
      showResetDialog: false,
    };
  },
  computed: {
    store() {
      return useTableConfigStore();
    },

    notificationStore() {
      return useNotificationStore();
    },

    enabledFieldsCount() {
      return this.editingFields.filter((f) => f.enabled).length;
    },

    availableFields() {
      const fields = [
        ...this.schemaColumns.map((column) => ({
          key: column.key,
          label: column.label || column.key,
        })),
        ...this.availableAttributes,
      ];
      const seen = new Set();
      return fields.filter((field) => {
        if (!field.key || seen.has(field.key)) return false;
        seen.add(field.key);
        return true;
      });
    },

    filteredAttributes() {
      const search = this.attributeSearch.toLowerCase();
      return this.availableFields.filter((attr) => {
        const exists = this.editingFields.some(
          (f) => f.key === attr.key
        );
        if (exists) return false;
        const label = (attr.label || "").toLowerCase();
        const key = (attr.key || "").toLowerCase();
        return label.includes(search) || key.includes(search);
      });
    },

    deleteConfigDialogMessage() {
      if (!this.configPendingDelete) return "";
      return `Конфигурация «${this.configPendingDelete.name}» будет удалена`;
    },
  },
  methods: {
    canDeleteConfig(config) {
      if (config.current) return false;
      const nonDefaultConfigs = this.configs.filter(
        (c) => !c.default
      );
      if (nonDefaultConfigs.length <= 1 && !config.default)
        return false;
      return true;
    },
    getDeleteTooltip(config) {
      if (config.current) return "Нельзя удалить активный конфиг";
      const nonDefaultConfigs = this.configs.filter(
        (c) => !c.default
      );
      if (nonDefaultConfigs.length <= 1 && !config.default)
        return "Нельзя удалить последний конфиг";
      return "Удалить конфигурацию";
    },
    async init() {
      this.isLoading = true;
      try {
        this.schemaColumns = await loadTableColumns(this.tableName);
        const res = await tableConfigsApi.index(this.companyId, {
          resource_model: this.resourceModel,
        });
        this.configs = res.data.result || [];
        const active =
          this.configs.find((c) => c.current) ||
          this.configs.find((c) => c.enabled) ||
          this.configs[0];
        if (active) {
          await this.selectConfig(active);
        } else {
          this.startNewConfig();
        }
        await this.loadAttributes();
      } catch (e) {
        console.error("Error loading settings:", e);
      } finally {
        this.isLoading = false;
      }
    },
    async loadAttributes() {
      const rType = this.recordType || this.resourceModel;
      this.loadingAttributes = true;
      try {
        const attributes = await this.store.getAvailableAttributes(
          this.companyId,
          rType
        );
        this.availableAttributes = attributes;
      } catch (e) {
        console.warn("Не удалось загрузить атрибуты:", e);
        this.availableAttributes = [];
      } finally {
        this.loadingAttributes = false;
      }
    },
    async selectConfig(config) {
      this.selectedConfigId = config.id;
      this.editingConfig = { ...config };
      this.isMobileEditing = true;
      try {
        const res = await tableConfigsApi.getFields(
          this.companyId,
          config.id
        );
        const serverFields = res.data.result || [];
        this.mergeFields(serverFields);
      } catch (e) {
        console.error(e);
      }
    },
    async createNewConfig() {
      this.isCreatingConfig = true;
      try {
        const configData = {
          name: "Новая конфигурация",
          default: false,
          resource_model: this.resourceModel,
          company_id: this.companyId,
        };
        const res = await tableConfigsApi.create(
          this.companyId,
          configData
        );
        const newConfig = res.data.result;
        this.configs.push(newConfig);
        await this.selectConfig(newConfig);
        this.notificationStore.showSuccess("Конфигурация создана");
      } catch (error) {
        console.error("Ошибка создания конфига:", error);
        this.notificationStore.showError(
          "Не удалось создать новую конфигурацию"
        );
      } finally {
        this.isCreatingConfig = false;
      }
    },
    startNewConfig() {
      this.selectedConfigId = null;
      this.editingConfig = {
        id: null,
        name: "Новая конфигурация",
        default: false,
        resource_model: this.resourceModel,
      };
      this.mergeFields([]);
      this.isMobileEditing = true;
    },
    mergeFields(serverFields) {
      let merged = serverFields.map((field, index) => {
        const isCustom =
          field.field_key.includes(".") ||
          !this.schemaColumns.some(
            (col) => col.key === field.field_key
          );
        return {
          key: field.field_key,
          id: field.id,
          label: field.label,
          width: field.width || 150,
          enabled: field.enabled !== false,
          position: field.position || index,
          isCustom: isCustom,
          isNewCustom: false,
          updating: false,
        };
      });
      merged.sort((a, b) => a.position - b.position);
      this.editingFields = merged;
      if (merged.length === 0) {
        this.editingFields = [];
      }
    },
    async applyConfig(config) {
      try {
        this.isLoading = true;
        await tableConfigsApi.setCurrent(this.companyId, config.id);
        const res = await tableConfigsApi.index(this.companyId, {
          resource_model: this.resourceModel,
        });
        this.configs = res.data.result || [];
        await this.selectConfig(config);

        this.$emit("config-saved");

        this.notificationStore.showSuccess(
          `Конфигурация «${config.name}» применена`
        );
      } catch (error) {
        console.error("Ошибка при применении конфига:", error);
        this.notificationStore.showError(
          "Не удалось применить конфигурацию"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async deleteConfig(config) {
      if (!this.canDeleteConfig(config)) {
        if (config.current) {
          this.notificationStore.showWarning(
            "Нельзя удалить активный конфиг. Сначала выберите другой как активный"
          );
        } else {
          this.notificationStore.showWarning(
            "Нельзя удалить последний конфиг"
          );
        }
        return;
      }
      this.configPendingDelete = config;
    },
    async handleDeleteConfigDialogAction(action) {
      if (action !== "confirm") {
        this.configPendingDelete = null;
        return;
      }

      const config = this.configPendingDelete;
      this.configPendingDelete = null;
      if (!config) return;

      try {
        await tableConfigsApi.delete(this.companyId, config.id);
        this.configs = this.configs.filter((c) => c.id !== config.id);
        if (this.selectedConfigId === config.id) {
          const nextConfig = this.configs[0];
          if (nextConfig) {
            await this.selectConfig(nextConfig);
          } else {
            this.startNewConfig();
          }
        }
        this.notificationStore.showSuccess(
          `Конфигурация «${config.name}» удалена`
        );
      } catch (e) {
        console.error("Ошибка удаления конфига:", e);
      }
    },
    async deleteField(field, index) {
      if (this.isDeletingField) {
        this.notificationStore.showWarning(
          "Дождитесь завершения операции"
        );
        return;
      }
      if (!field.id) {
        this.editingFields.splice(index, 1);
        return;
      }
      this.isDeletingField = true;
      try {
        await tableConfigsApi.deleteField(
          this.companyId,
          this.editingConfig.id,
          field.id
        );
        this.editingFields.splice(index, 1);
        this.notificationStore.showSuccess("Поле удалено");
      } catch (error) {
        console.error("Ошибка удаления поля:", error);
        this.notificationStore.showError(
          "Не удалось удалить поле из конфигурации"
        );
      } finally {
        this.isDeletingField = false;
      }
    },
    addAttribute(attr) {
      const alreadyExists = this.editingFields.some(
        (field) => field.key === attr.key
      );
      if (alreadyExists) {
        const index = this.editingFields.findIndex(
          (f) => f.key === attr.key
        );
        if (index !== -1) {
          this.editingFields[index].enabled = true;
          this.editingFields[index].isNewCustom = false;
        }
        return;
      }
      const newField = {
        key: attr.key,
        label: attr.label || attr.key,
        width: 150,
        enabled: true,
        position: this.editingFields.length,
        id: null,
        isCustom: true,
        isNewCustom: true,
        updating: false,
      };
      this.editingFields.push(newField);
    },
    removeNewCustomField(index) {
      const field = this.editingFields[index];
      if (field.isNewCustom) {
        this.editingFields.splice(index, 1);
      }
    },
    async updateFieldLabel(field) {
      if (!field.id || field.isNewCustom) return;
      try {
        field.updating = true;
        await tableConfigsApi.updateField(
          this.companyId,
          this.editingConfig.id,
          field.id,
          {
            label: field.label,
            enabled: field.enabled,
            width: field.width,
            position: field.position,
          }
        );
      } catch (error) {
        console.error("Ошибка обновления названия поля:", error);
      } finally {
        field.updating = false;
      }
    },
    async updateFieldWidth(field) {
      if (!field.id || field.isNewCustom) return;
      try {
        field.updating = true;
        await tableConfigsApi.updateField(
          this.companyId,
          this.editingConfig.id,
          field.id,
          {
            label: field.label,
            enabled: field.enabled,
            width: parseInt(field.width) || 150,
            position: field.position,
          }
        );
      } catch (error) {
        console.error("Ошибка обновления ширины поля:", error);
      } finally {
        field.updating = false;
      }
    },
    async toggleFieldEnabled(field) {
      if (!field.id || field.isNewCustom) return;
      try {
        field.updating = true;
        if (field.enabled) {
          await tableConfigsApi.enableField(
            this.companyId,
            this.editingConfig.id,
            field.id
          );
        } else {
          await tableConfigsApi.disableField(
            this.companyId,
            this.editingConfig.id,
            field.id
          );
        }
        this.$emit("config-saved");

        this.notificationStore.showSuccess(
          field.enabled ? "Поле включено" : "Поле отключено"
        );
      } catch (error) {
        console.error("Ошибка переключения поля:", error);
        field.enabled = !field.enabled;
      } finally {
        field.updating = false;
      }
    },
    async resetToDefaults() {
      if (!this.editingConfig.id) {
        this.editingFields = [];
        return;
      }
      this.showResetDialog = true;
    },
    async handleResetDialogAction(action) {
      this.showResetDialog = false;
      if (action !== "confirm") return;

      try {
        this.isLoading = true;
        await tableConfigsApi.reset(
          this.companyId,
          this.editingConfig.id
        );
        const res = await tableConfigsApi.getFields(
          this.companyId,
          this.editingConfig.id
        );
        const serverFields = res.data.result || [];

        this.mergeFields(serverFields);
        this.$emit("config-saved");

        this.notificationStore.showSuccess(
          "Конфигурация сброшена до значений по умолчанию"
        );
      } catch (error) {
        console.error("Ошибка при сбросе конфигурации:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async saveConfig() {
      if (this.isDeletingField) return;
      this.isSaving = true;
      try {
        const configPayload = {
          ...this.editingConfig,
          company_id: this.companyId,
          resource_model: this.resourceModel,
        };
        let configId;
        if (this.editingConfig.id) {
          await tableConfigsApi.update(
            this.companyId,
            this.editingConfig.id,
            {
              name: this.editingConfig.name,
              default: this.editingConfig.default,
            }
          );
          configId = this.editingConfig.id;
        } else {
          const res = await tableConfigsApi.create(
            this.companyId,
            configPayload
          );
          configId = res.data.result.id;
        }
        const savePromises = this.editingFields
          .filter((field) => field.enabled)
          .map(async (field, index) => {
            const payload = {
              field_key: field.key,
              label: field.label,
              position: index,
              enabled: field.enabled,
              width: parseInt(field.width) || 150,
            };
            if (field.id) {
              return tableConfigsApi.updateField(
                this.companyId,
                configId,
                field.id,
                payload
              );
            } else {
              return tableConfigsApi.createField(
                this.companyId,
                configId,
                payload
              );
            }
          });
        await Promise.all(savePromises);
        await tableConfigsApi.enable(this.companyId, configId);
        this.store.clearAttributesCache(
          this.companyId,
          this.recordType || this.resourceModel
        );
        const configsRes = await tableConfigsApi.index(
          this.companyId,
          {
            resource_model: this.resourceModel,
          }
        );
        this.configs = configsRes.data.result || [];
        const currentConfig = this.configs.find(
          (c) => c.id === configId
        );
        if (currentConfig) {
          const fieldsRes = await tableConfigsApi.getFields(
            this.companyId,
            configId
          );
          const serverFields = fieldsRes.data.result || [];
          this.mergeFields(serverFields);
        }
        this.$emit("config-saved");
        this.notificationStore.showSuccess("Настройки сохранены");
      } catch (e) {
        console.error("Save failed", e);
        this.notificationStore.showError("Ошибка при сохранении");
      } finally {
        this.isSaving = false;
      }
    },
    onDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.dropEffect = "move";
    },
    onDrop(event, index) {
      if (this.draggedIndex === null) return;
      const itemToMove = this.editingFields[this.draggedIndex];
      this.editingFields.splice(this.draggedIndex, 1);
      this.editingFields.splice(index, 0, itemToMove);
      this.draggedIndex = null;
    },
  },
  async mounted() {
    if (!this.store.apiAvailable) {
      this.notificationStore.showError(
        "API недоступен. Используется локальный режим"
      );
      this.$emit("close");
      return;
    }
    await this.init();
  },
};
</script>

<style scoped>
.settings-body {
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;
  background: #fcfcfc;
}

.sidebar {
  width: 260px;
  border-right: 1px solid var(--color-grey);
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.sidebar-header {
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.sidebar-header h4 {
  font-size: 14px;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin: 0;
}

.add-btn {
  padding: 2px 10px;
  font-size: 20px;
  line-height: 24px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
}

.config-list {
  overflow-y: auto;
  flex: 1;
}

.config-item {
  padding: 14px 20px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  transition: background 0.2s;
}

.config-item:hover {
  background: #eef2ff;
}

.config-item.active {
  background: #eef7ff;
  border-left: 4px solid var(--color-blue);
}

.config-item.current-config {
  background: #f0f9f0;
  border-left: 4px solid #10b981;
}

.config-item.current-config.active {
  background: #e6f7e6;
  border-left: 4px solid #10b981;
}

.config-name {
  font-weight: 500;
  font-size: 15px;
  color: var(--color-dark);
}

.badges {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.badge {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-default {
  background: #e0e0e0;
  color: #555;
}

.badge-current {
  background: #10b981;
  color: white;
}

.config-actions {
  display: flex;
  gap: 4px;
}

.icon-btn {
  background: none;
  border: none;
  opacity: 0.4;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-btn:hover {
  opacity: 1;
  border-radius: 4px;
}

.icon-btn.apply-btn {
  color: #10b981;
}

.icon-btn.apply-btn:hover {
  background: #d1fae5;
}

.icon-btn.delete {
  color: var(--color-danger);
}

.icon-btn.delete:hover {
  background: #fee2e2;
}

.icon-btn.delete:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.icon-btn.delete:disabled:hover {
  background: none;
}

.editor-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  min-width: 0;
  height: 100%;
}

.editor-form {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.editor-header-inputs {
  padding: 20px 24px;
  background: #fff;
  border-bottom: 1px solid var(--color-grey);
  display: flex;
  align-items: flex-end;
  gap: 24px;
  flex-shrink: 0;
}

.name-input {
  flex: 1;
  margin-bottom: 0 !important;
}

.editor-workspace {
  flex: 1;
  display: flex;
  overflow: hidden;
  background: #f4f5f7;
  gap: 1px;
}

.workspace-col {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  height: 100%;
}

.active-fields-col {
  flex: 3;
  border-right: 1px solid var(--color-grey);
}

.available-fields-col {
  flex: 2;
  background: #fdfdfd;
}

.col-header {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-grey);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fbfbfb;
  flex-shrink: 0;
}

.col-header h5 {
  margin: 0;
  font-size: 13px;
  text-transform: uppercase;
  color: #777;
  font-weight: 600;
}

.col-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.count {
  background: #eee;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  color: #555;
  min-width: 24px;
  text-align: center;
}

.fields-list-labels {
  display: flex;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
}

.label-col {
  flex: 1;
  padding-left: 24px;
}

.label-props {
  display: flex;
  gap: 45px;
  margin-right: 75px;
}

.custom-scroll {
  overflow-y: auto;
  flex: 1;
  padding: 10px;
}

.custom-scroll::-webkit-scrollbar {
  width: 6px;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.field-item {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  margin-bottom: 6px;
  border-radius: 6px;
  transition: all 0.2s;
  position: relative;
}

.field-item:hover {
  border-color: var(--color-blue);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.drag-handle {
  padding: 10px;
  color: #ccc;
  cursor: grab;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  color: #666;
}

.field-content {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 8px 10px 8px 0;
  gap: 15px;
}

.field-main {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-name-input {
  width: 100%;
  border: none;
  font-size: 14px;
  color: var(--color-dark);
  background: transparent;
  padding: 4px;
  border-bottom: 1px dashed transparent;
}

.field-name-input:focus {
  outline: none;
  border-bottom-color: var(--color-blue);
}

.field-name-input::placeholder {
  color: #aaa;
  font-style: italic;
}

.field-delete-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: var(--color-danger);
  cursor: pointer;
  padding: 4px 8px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  opacity: 0.5;
  transition: all 0.2s;
  margin-left: 10px;
}

.field-delete-btn:hover {
  opacity: 1;
  background: #fee2e2;
}

.field-props {
  display: flex;
  align-items: center;
  gap: 15px;
}

.width-input {
  width: 50px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px;
  font-size: 13px;
}

.toggle-switch {
  position: relative;
  width: 34px;
  height: 18px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--color-green);
}

input:checked + .slider:before {
  transform: translateX(16px);
}

.switch-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.toggle-switch:has(input:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-switch:has(input:disabled) .slider {
  cursor: not-allowed;
}

.always-enabled-indicator {
  width: 34px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-green);
  border-radius: 34px;
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.search-box {
  padding: 10px;
  border-bottom: 1px solid #eee;
  position: relative;
  flex-shrink: 0;
}

.search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.search-box input {
  width: 100%;
  padding: 8px 8px 8px 30px;
  border: 1px solid #eee;
  border-radius: 6px;
  font-size: 13px;
  background: #f9f9f9;
}

.search-box input:focus {
  background: #fff;
  border-color: var(--color-blue);
  outline: none;
}

.attributes-list {
  padding: 10px;
}

.attribute-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.attribute-item:hover {
  border-color: var(--color-blue);
  background: #f0f7ff;
  transform: translateX(-2px);
}

.attr-icon {
  width: 20px;
  height: 20px;
  background: #eef2ff;
  color: var(--color-blue);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  line-height: 1;
}

.attr-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.attr-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attr-key {
  font-size: 11px;
  color: #999;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-search,
.empty-placeholder {
  text-align: center;
  padding: 30px 10px;
  color: #999;
  font-size: 13px;
  font-style: italic;
}

/* Стики-футер */
.sticky-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--color-grey);
  background: #fff;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.reset-btn {
  background: #fff;
  border: 1px solid var(--color-danger);
  color: var(--color-danger);
}

.reset-btn:hover {
  background: #fff5f5;
}

.mobile-nav {
  display: none;
}

.mobile-hidden {
  display: flex;
}

.mobile-visible {
  display: flex;
}

@media screen and (max-width: 900px) {
  .editor-workspace {
    flex-direction: column;
  }
  .active-fields-col {
    flex: 1;
    border-right: none;
    border-bottom: 1px solid var(--color-grey);
  }
  .available-fields-col {
    flex: 1;
    background: #fdfdfd;
  }
}

@media screen and (max-width: 768px) {
  .settings-modal {
    width: 100vw;
    height: 100dvh;
    height: 100vh;
    max-height: none;
    max-width: none;
    border-radius: 0;
    margin: 0;
  }

  .settings-body {
    position: relative;
  }

  .sidebar,
  .editor-panel {
    width: 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    background: #fff;
  }

  .mobile-hidden {
    display: none;
  }

  .sidebar:not(.mobile-hidden) {
    display: flex;
    z-index: 2;
  }

  .mobile-visible {
    display: flex;
  }

  .editor-panel:not(.mobile-visible) {
    display: none;
  }

  .mobile-nav {
    display: flex;
    padding: 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #eee;
    flex-shrink: 0;
  }
  .mobile-nav button {
    background: none;
    border: none;
    color: var(--color-blue);
    font-weight: 600;
    font-size: 14px;
  }

  .editor-header-inputs {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
    padding: 16px;
  }

  .sticky-footer {
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
    justify-content: space-between;
  }

  .sticky-footer button {
    flex: 1;
    display: flex;
    justify-content: center;
  }

  .fields-list-labels {
    display: none;
  }
  .label-props {
    gap: 10px;
  }

  .field-content {
    flex-wrap: wrap;
  }
  .field-main {
    width: 100%;
    margin-bottom: 8px;
  }
  .field-props {
    width: 100%;
    justify-content: space-between;
  }

  .active-fields-col {
    flex: 3;
    min-height: 200px;
  }

  .available-fields-col {
    flex: 2;
    border-top: 1px solid var(--color-grey);
    max-height: none;
  }
}
</style>
